import { Feature, MultiDeptValue } from '../../../types/feature';
import { Role } from '../../../types/role';

export type BU = 'OMS' | 'AMS' | 'EPC';
export const ALL_BUS: BU[] = ['OMS', 'AMS', 'EPC'];

export interface RoleActionEntry {
  roleId: string;
  roleName: string;
  description?: string;
  isDefault?: boolean;
  actions: string[];
}

export interface FeatureBUEntry {
  /** Union of all granted actions across roles in this BU. */
  aggregatedActions: string[];
  /** Roles in this BU that have at least one action granted on this feature. */
  rolesWithAccess: RoleActionEntry[];
  /** Roles in this BU that have no access yet (for the expanded view). */
  rolesWithoutAccess: RoleActionEntry[];
}

export interface FeatureMatrixEntry {
  /** Which BUs this feature targets (the columns we show as "active"). */
  targetBUs: BU[];
  /** Per-BU role permission breakdown. Only includes targetBUs. */
  byBU: Partial<Record<BU, FeatureBUEntry>>;
}

export type FeatureRoleMatrix = Record<string, FeatureMatrixEntry>;

export interface OrphanPermission {
  roleId: string;
  roleName: string;
  module: string;
  actions: string[];
}

/** Extract the module key used in `role.permissions` from a feature's permissionKey. */
export function getFeatureModuleKey(feature: Feature): string {
  return feature.permissionKey.split('.')[0];
}

/** Which BUs is this feature visible to? COMMON → all three. */
export function getFeatureTargetBUs(feature: Feature): BU[] {
  const ids =
    feature.businessUnitIds ??
    (feature as Feature & { departmentIds?: MultiDeptValue[] }).departmentIds;
  if (ids && ids.length > 0) return [...new Set(ids as BU[])];

  const units =
    feature.businessUnits ??
    (feature as Feature & { departments?: MultiDeptValue[] }).departments;
  if (units && units.length > 0) return [...new Set(units as BU[])];

  const bu =
    feature.businessUnit ??
    (feature as Feature & { department?: 'OMS' | 'AMS' | 'EPC' | 'COMMON' }).department ??
    'COMMON';
  return bu === 'COMMON' ? [...ALL_BUS] : [bu as BU];
}

function toEntry(r: Role, actions: string[]): RoleActionEntry {
  return {
    roleId: (r._id ?? r.id) as string,
    roleName: r.name,
    description: r.description,
    isDefault: r.isDefault,
    actions,
  };
}

/**
 * For a list of features and the current role set, return a per-feature, per-BU
 * breakdown of which roles have what actions granted on that feature's module.
 */
export function buildFeatureRoleMatrix(
  features: Feature[],
  roles: Role[],
): FeatureRoleMatrix {
  const activeRoles = roles.filter(r => r.isActive !== false);

  const rolesByBU: Record<BU, Role[]> = { OMS: [], AMS: [], EPC: [] };
  for (const r of activeRoles) {
    if (r.department === 'OMS' || r.department === 'AMS' || r.department === 'EPC') {
      rolesByBU[r.department].push(r);
    }
  }

  const sortRoles = (a: RoleActionEntry, b: RoleActionEntry) => {
    if (!!a.isDefault !== !!b.isDefault) return a.isDefault ? -1 : 1;
    return a.roleName.localeCompare(b.roleName);
  };

  const result: FeatureRoleMatrix = {};
  for (const f of features) {
    const moduleKey = getFeatureModuleKey(f);
    const targetBUs = getFeatureTargetBUs(f);
    const byBU: Partial<Record<BU, FeatureBUEntry>> = {};

    for (const bu of targetBUs) {
      const aggregated = new Set<string>();
      const withAccess: RoleActionEntry[] = [];
      const withoutAccess: RoleActionEntry[] = [];

      for (const r of rolesByBU[bu]) {
        const actions = r.permissions?.[moduleKey] ?? [];
        if (actions.length > 0) {
          actions.forEach(a => aggregated.add(a));
          withAccess.push(toEntry(r, actions));
        } else {
          withoutAccess.push(toEntry(r, []));
        }
      }

      withAccess.sort(sortRoles);
      withoutAccess.sort(sortRoles);

      byBU[bu] = {
        aggregatedActions: Array.from(aggregated),
        rolesWithAccess: withAccess,
        rolesWithoutAccess: withoutAccess,
      };
    }

    result[f.key] = { targetBUs, byBU };
  }
  return result;
}

/** Compute the next permissions map after toggling a single (module, action) on a role. */
export function nextPermissionsAfterToggle(
  current: Role['permissions'] | undefined,
  moduleKey: string,
  action: string,
  willBeIncluded: boolean,
): Role['permissions'] {
  const next: Role['permissions'] = { ...(current ?? {}) };
  const existing = new Set(next[moduleKey] ?? []);
  if (willBeIncluded) existing.add(action);
  else existing.delete(action);

  if (existing.size === 0) {
    delete next[moduleKey];
  } else {
    next[moduleKey] = Array.from(existing);
  }
  return next;
}

/**
 * Find roles that hold permissions on modules that no longer have an active feature.
 * Useful for surfacing stale/dangling permission grants in the Features UI.
 */
export function detectOrphanPermissions(
  features: Feature[],
  roles: Role[],
): OrphanPermission[] {
  const featureModuleSet = new Set<string>();
  for (const f of features) {
    featureModuleSet.add(getFeatureModuleKey(f));
  }

  const orphans: OrphanPermission[] = [];
  for (const r of roles) {
    if (r.isActive === false) continue;
    const permissions = r.permissions ?? {};
    for (const [module, actions] of Object.entries(permissions)) {
      if (module === '*') continue;
      if (!actions || actions.length === 0) continue;
      if (featureModuleSet.has(module)) continue;
      orphans.push({
        roleId: (r._id ?? r.id) as string,
        roleName: r.name,
        module,
        actions,
      });
    }
  }

  orphans.sort((a, b) => {
    const byName = a.roleName.localeCompare(b.roleName);
    if (byName !== 0) return byName;
    return a.module.localeCompare(b.module);
  });

  return orphans;
}
